import { useState } from 'react'
import { CheckCircle, Plus, PlusSquare } from 'react-feather'
import { useHistory } from 'react-router-dom'
import clsx from 'clsx'

import {
  Menu,
  MenuItem,
  MenuDivider,
  SubMenu,
  useAuthState,
  useGlobalState,
  Dropdown,
  Avatar,
} from '@pepper/ui'

import { OrgModalType } from 'container/Dashboard/Dashboard'
import CreateOrganisationJourney from 'container/CreateOrganisationJourney/CreateOrganisationJourney'
import useOrganisations from 'utils/hooks/useOrganisations'

import s from './UserDropdown.module.css'

const DEFAULT_VISIBLE_COMPANIES = 3

type UserDropdownProps = {
  onClose: () => void
  setJoinOrganisationModal?: (args: OrgModalType) => void
}

const UserDropdown = ({
  onClose,
  setJoinOrganisationModal,
}: UserDropdownProps) => {
  const { permissions } = useGlobalState()
  const history = useHistory()
  const { user, logout } = useAuthState()
  const { pathname } = history.location
  const isInvitesRoute = pathname === '/invites'
  const isCreateOrganisationRoute = pathname === '/create-organisation'
  const { selectedCompany, allCompanies, onSelect } = useOrganisations(
    user,
    setJoinOrganisationModal
  )
  const [createOrganisationModal, setCreateOrganisationModal] =
    useState<boolean>(false)

  const handleOpenCreateOrganisationModal = () => {
    setCreateOrganisationModal(true)
  }

  const handleLogout = (): void => {
    logout()
    window.location.href = '/'
  }

  const menuItems = [
    {
      title: 'Personal Settings',
      path: '/personal-settings/profile',
      dataId: 'personalSettings',
    },
    {
      title: 'Organisation Settings',
      path: '/admin-console/organisation',
      dataId: 'adminConsole',
    },
  ]

  if (permissions?.marketplace) {
    menuItems.push({
      title: 'Invoices',
      path: '/invoices',
      dataId: 'invoices',
    })
  }

  const createOrganisationMenuItem = (
    <MenuItem
      className={clsx(s.menuItem, s.createOrganisationMenuItem)}
      onClick={handleOpenCreateOrganisationModal}
      data-id="business-topbar-create-new-organisation-menu-item"
    >
      <span>Create Organisation</span>
      <PlusSquare size={16} />
    </MenuItem>
  )

  const UserDetailMenuItem = (
    <MenuItem className={clsx(s.UserProfileDescription)}>
      <div className={s.name}>{user?.first_name || 'Me'}</div>
      {!!selectedCompany && (
        <div
          className={clsx(s.companyName, s.truncate)}
          title={user?.email || ''}
        >
          {user?.email || ''}
        </div>
      )}
    </MenuItem>
  )

  const logoutMenuItem = (
    <MenuItem
      className={s.menuItem}
      onClick={handleLogout}
      data-id="business-topbar-logout-menu-item"
    >
      <span>Logout</span>
    </MenuItem>
  )

  if (isCreateOrganisationRoute) {
    return (
      <Menu className={s.dropdownMenu}>
        {createOrganisationMenuItem}
        <MenuDivider className={s.divider} />
        {logoutMenuItem}
      </Menu>
    )
  }

  const companyMenuItemGroup = () => {
    return (
      <>
        <MenuItem
          className={clsx(s.menuItem, s.createOrganisationMenuItem)}
          onClick={handleOpenCreateOrganisationModal}
          data-id="business-topbar-create-new-organisation-menu-item"
        >
          <span>{`Organisations (${allCompanies?.length})`}</span>
          <Plus size={16} />
        </MenuItem>
        {selectedCompany && (
          <MenuItem
            key={selectedCompany.id}
            className={clsx(s.menuItem, s.activeMenuItem)}
            disabled={true}
            data-id="business-topbar-organisation-list-item"
          >
            <span className={s.truncate}>{selectedCompany.company_name}</span>
            <CheckCircle size={16} color="#1600E5" />
          </MenuItem>
        )}
        {allCompanies?.slice(0, DEFAULT_VISIBLE_COMPANIES - 1).map(company => {
          if (company.id === selectedCompany?.id) {
            return null
          }
          return (
            <MenuItem
              key={company.id}
              className={clsx(s.menuItem)}
              onClick={() => onSelect(company)}
              data-id="business-topbar-organisation-list-item"
            >
              <span className={s.truncate}>{company.company_name}</span>
              {company.invite_id && (
                <span className={s.menuItemSmallText}>PENDING</span>
              )}
            </MenuItem>
          )
        })}
        {allCompanies && allCompanies.length >= DEFAULT_VISIBLE_COMPANIES && (
          <SubMenu
            title="View All Organisations"
            popupClassName={s.subMenuPopup}
            data-id="business-topbar-view-all-organisation-menu-item"
          >
            {allCompanies.slice(DEFAULT_VISIBLE_COMPANIES - 1).map(company => {
              if (company.id === selectedCompany?.id) {
                return null
              }
              return (
                <MenuItem
                  key={company.id}
                  className={s.menuItem}
                  onClick={() => onSelect(company)}
                  data-id="business-topbar-organisation-list-item"
                >
                  <span className={s.truncate}>{company.company_name}</span>
                  {company.invite_id && (
                    <span className={s.menuItemSmallText}>PENDING</span>
                  )}
                </MenuItem>
              )
            })}
          </SubMenu>
        )}
      </>
    )
  }

  if (isInvitesRoute) {
    return (
      <Menu className={s.dropdownMenu}>
        {createOrganisationMenuItem}
        <MenuDivider className={s.divider} />
        {companyMenuItemGroup()}
        <MenuDivider className={s.divider} />
        {logoutMenuItem}
      </Menu>
    )
  }

  return (
    <>
      <Menu data-testid="topbar-dropdown-menu" className={s.dropdownMenu}>
        {UserDetailMenuItem}
        <MenuDivider className={s.divider} />
        {companyMenuItemGroup()}
        <MenuDivider className={s.divider} />
        {menuItems.map(item => (
          <MenuItem
            onClick={() => {
              onClose()
              history.push(item.path)
            }}
            className={s.menuItem}
            key={item.path}
            data-id={item.dataId}
          >
            <span>{item.title}</span>
          </MenuItem>
        ))}

        <MenuDivider className={s.divider} />
        {logoutMenuItem}
      </Menu>
      <CreateOrganisationJourney
        modal={createOrganisationModal}
        setModal={setCreateOrganisationModal}
      />
    </>
  )
}

export const UserDropdownContainer = ({
  setJoinOrganisationModal,
}: {
  setJoinOrganisationModal?: (args: OrgModalType) => void
}) => {
  const { user } = useAuthState()
  const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false)

  return (
    <Dropdown
      trigger={['click']}
      overlay={
        <UserDropdown
          onClose={() => setIsUserDropdownOpen(false)}
          setJoinOrganisationModal={setJoinOrganisationModal}
        />
      }
      placement="topRight"
      visible={isUserDropdownOpen}
      onVisibleChange={isOpen => setIsUserDropdownOpen(isOpen)}
    >
      <div>
        <button
          className={s.avatarButton}
          data-id="business-topbar-avatar-button"
        >
          <Avatar size="default" src={user.avatar} className={s.avatar}>
            {user.first_name?.trim()[0]?.toUpperCase()}
          </Avatar>
        </button>
      </div>
    </Dropdown>
  )
}
